import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { PatternSelector } from '@/components/workflows/pattern-selector'
import { useWorkflowStore } from '@/stores/workflow-store'
import { useOrganizationStore } from '@/stores/organization-store'
import { workflowCreateSchema, type WorkflowCreateInput, type WorkflowPattern } from '@/schemas/workflow'

interface CreateWorkflowDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CreateWorkflowDialog({ open, onOpenChange }: CreateWorkflowDialogProps) {
  const { t } = useTranslation('workflows')
  const navigate = useNavigate()
  const { createWorkflow } = useWorkflowStore()
  const { currentProject } = useOrganizationStore()
  const [step, setStep] = useState<1 | 2>(1)
  const [selectedPattern, setSelectedPattern] = useState<WorkflowPattern | undefined>()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<WorkflowCreateInput>({
    resolver: zodResolver(workflowCreateSchema),
  })

  const handlePatternSelect = (pattern: WorkflowPattern) => {
    setSelectedPattern(pattern)
    setStep(2)
  }

  const handleBack = () => {
    setStep(1)
  }

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      setStep(1)
      setSelectedPattern(undefined)
      reset()
    }
    onOpenChange(isOpen)
  }

  const onSubmit = async (data: WorkflowCreateInput) => {
    if (!currentProject?.id || !selectedPattern) return

    setIsSubmitting(true)
    try {
      const workflow = await createWorkflow(currentProject.id, {
        ...data,
        pattern_type: selectedPattern,
      })
      handleClose(false)
      navigate(`/workflows/${workflow.id}`)
    } catch {
      // Error handled by store
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="glass border-gray-700/50 sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="text-gray-100">
            {t('createDialog.title')}
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            {step === 1 ? t('createDialog.selectPattern') : t('createDialog.nameLabel')}
          </DialogDescription>
        </DialogHeader>

        {step === 1 ? (
          <PatternSelector
            onSelect={handlePatternSelect}
            selected={selectedPattern}
          />
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <input type="hidden" value={selectedPattern} {...register('pattern_type')} />

            <div className="space-y-2">
              <Label htmlFor="workflow-name" className="text-gray-300">
                {t('createDialog.nameLabel')}
              </Label>
              <Input
                id="workflow-name"
                placeholder={t('createDialog.namePlaceholder')}
                className="bg-white/5 border-gray-700/50 focus:border-indigo-500/50"
                {...register('name')}
              />
              {errors.name && (
                <p className="text-sm text-red-400">{errors.name.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="workflow-description" className="text-gray-300">
                {t('createDialog.descriptionLabel')}
              </Label>
              <Textarea
                id="workflow-description"
                placeholder={t('createDialog.descriptionPlaceholder')}
                className="bg-white/5 border-gray-700/50 focus:border-indigo-500/50 min-h-[80px]"
                {...register('description')}
              />
              {errors.description && (
                <p className="text-sm text-red-400">{errors.description.message}</p>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleBack}
                className="border-gray-700/50 text-gray-300"
              >
                {t('createDialog.backButton')}
              </Button>
              <Button
                type="submit"
                variant="action-indigo"
                disabled={isSubmitting}
              >
                {isSubmitting ? '...' : t('createDialog.createButton')}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
